/**
 * SchoolRegistryPanel — het register zelf: aanmaken en verifiëren.
 *
 * WAAROM "OPNEMEN" EN "VERIFIËREN" TWEE HANDELINGEN ZIJN
 *
 * Een school aanmaken is administratie. Hem op `verified` zetten maakt hem
 * zichtbaar voor élke registrerende leerkracht in het land, en dat hoort een
 * eigen beslissing te zijn met een eigen voorwaarde: is de
 * verwerkersovereenkomst rond? Zou opnemen automatisch goedkeuren betekenen,
 * dan is er niets meer te verifiëren en is het register een lijst met
 * doorgeefluiken.
 *
 * WAAROM DE ONTBREKENDE OVEREENKOMST ZICHTBAAR IS EN GEEN BLOKKADE
 *
 * De tabel dwingt `dpa_signed_at` niet af, omdat een school soms vooruitlopend
 * wordt opgenomen (de overeenkomst is onderweg). Maar een geverifieerde school
 * zonder ondertekende overeenkomst is precies het gat waarop je later wordt
 * aangesproken, dus het scherm zegt het hardop bij die rij.
 */

import React, { useState } from 'react';
import { Building2, Plus, ShieldCheck, ShieldX, AlertTriangle, RotateCcw } from 'lucide-react';
import { upsertSchool, setSchoolStatus, type School, type SchoolStatus } from '../../../../data/supabase/schoolRegistry';
interface SchoolRegistryPanelProps {
  schools: School[];
  loading: boolean;
  error: string | null;
  onChanged: () => void;
}
const STATUS_LABEL: Record<SchoolStatus, string> = {
  pending: 'Onbevestigd',
  verified: 'Geverifieerd',
  rejected: 'Afgewezen'
};
const STATUS_STYLE: Record<SchoolStatus, string> = {
  pending: 'bg-amber-100 text-amber-900',
  verified: 'bg-emerald-100 text-emerald-900',
  rejected: 'bg-slate-200 text-slate-700'
};
export function SchoolRegistryPanel({
  schools,
  loading,
  error,
  onChanged
}: SchoolRegistryPanelProps) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [brin, setBrin] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const handleCreate = async () => {
    if (name.trim().length < 2) {
      setFormError('Vul een schoolnaam in.');
      return;
    }
    setBusy('form');
    setFormError(null);
    setNotice(null);
    const res = await upsertSchool({
      name: name.trim(),
      city: city.trim() || undefined,
      brin: brin.trim() || undefined
    });
    setBusy(null);
    if (!res.ok) {
      setFormError(res.message ?? 'De school is niet aangemaakt.');
      return;
    }
    setName('');
    setCity('');
    setBrin('');
    setShowForm(false);
    // Expliciet benoemen dat hij nog niet zichtbaar is. Zonder die regel
    // verwacht een beheerder de school direct bij een registratie te zien.
    setNotice('De school staat in het register op onbevestigd. Hij is nog niet te kiezen bij een registratie — dat gebeurt pas na verifiëren.');
    onChanged();
  };
  const handleStatus = async (school: School, status: SchoolStatus) => {
    setBusy(school.id);
    setFormError(null);
    setNotice(null);
    const res = await setSchoolStatus(school.id, status);
    setBusy(null);
    if (!res.ok) {
      setFormError(res.message ?? 'De status is niet gewijzigd.');
      return;
    }
    setNotice(status === 'verified' ? `${school.name} is geverifieerd en verschijnt nu als keuze bij het registreren.` : status === 'rejected' ? `${school.name} is afgewezen en verdwijnt uit de keuzelijst. Leerkrachten die eraan hangen houden hun account, maar geen schoolbinding.` : `${school.name} staat weer op onbevestigd en is niet meer te kiezen.`);
    onChanged();
  };
  return <section aria-labelledby="registry-heading" className="rounded-xl border border-slate-200 bg-white p-5" data-id="element-7316" data-mp="2s1ilcsraesk9">
      <div className="flex flex-wrap items-start justify-between gap-4" data-id="element-7317" data-mp="2wztut2do711y">
        <div data-id="element-7318" data-mp="1gmvjyzsodyff">
          <h2 id="registry-heading" className="text-base font-bold text-slate-900" data-id="element-7319" data-mp="1gmdhvmjc0pzl">
            Het register
          </h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600" data-id="element-7320" data-mp="1lnjcjpwjynbr">
            Alleen geverifieerde scholen worden aan een registrerende leerkracht
            als keuze aangeboden. Een leerkracht kan hier zelf niets aan
            toevoegen — dat is de hele reden dat verifiëren iets betekent.
          </p>
        </div>
        <button onClick={() => setShowForm(v => !v)} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-bold text-white transition-colors hover:bg-slate-800" data-id="element-7321" data-mp="1n4qdyz3u2e7x">
          <Plus className="h-4 w-4" aria-hidden="true" data-id="element-7322" data-mp="2fsm7xw20ps10" />
          School toevoegen
        </button>
      </div>

      {showForm && <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4" data-id="element-7323" data-mp="2asrfho2lgudu">
          <div className="grid gap-3 sm:grid-cols-3" data-id="element-7324" data-mp="25vr7h7m6phk3">
            <label className="block" data-id="element-7325" data-mp="25xc3z2s27akl">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-7326" data-mp="zf5sa8gap2uy">
                Naam
              </span>
              <input value={name} onChange={e => setName(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="OBS De Regenboog" data-id="element-7327" data-mp="ue9e9228c0bt" />
            </label>
            <label className="block" data-id="element-7328" data-mp="3b2setx1kolhs">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-7329" data-mp="3g1c68cyxqphj">
                Plaats
              </span>
              <input value={city} onChange={e => setCity(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="Utrecht" data-id="element-7330" data-mp="3kt3x6om06x87" />
            </label>
            <label className="block" data-id="element-7331" data-mp="3trz3s1sym9c1">
              <span className="text-xs font-bold uppercase tracking-wide text-slate-500" data-id="element-7332" data-mp="3tqxil4i8c6gl">
                BRIN
              </span>
              <input value={brin} onChange={e => setBrin(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" placeholder="12AB" data-id="element-7333" data-mp="7laahzl0dwm4" />
            </label>
          </div>
          <p className="mt-2 text-xs leading-relaxed text-slate-500" data-id="element-7334" data-mp="29cnbwenrpmdf">
            Plaats is wat twee scholen met dezelfde naam onderscheidt — vul hem
            in, ook als hij nu niet nodig lijkt. BRIN mag leeg blijven; niet elke
            aanbieder heeft er een.
          </p>
          <button onClick={() => void handleCreate()} disabled={busy === 'form'} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-bold text-white transition-colors hover:bg-indigo-700 disabled:opacity-60" data-id="element-7335" data-mp="2badnildf8rxd">
            {busy === 'form' ? 'Bezig…' : 'Opnemen in register'}
          </button>
        </div>}

      {notice && <p role="status" className="mt-4 rounded-lg bg-emerald-50 p-3 text-sm leading-relaxed text-emerald-900" data-id="element-7336" data-mp="2g8yoin0hzfmv">
          {notice}
        </p>}

      {formError && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm leading-relaxed text-rose-900" data-id="element-7337" data-mp="2p1a2h9efdaly">
          {formError}
        </p>}

      {loading && <p className="mt-4 text-sm text-slate-500" data-id="element-7338" data-mp="33zas5lopmq7n">Register ophalen…</p>}

      {error && <p role="alert" className="mt-4 flex gap-2 rounded-lg bg-rose-50 p-3 text-sm leading-relaxed text-rose-900" data-id="element-7339" data-mp="2z19e5f655h31">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" data-id="element-7340" data-mp="3imj78xl53fow" />
          <span data-id="element-7341" data-mp="3mmjy5hpfdjcu">{error}</span>
        </p>}

      {!loading && !error && schools.length === 0 && <div className="mt-4 rounded-lg bg-slate-50 p-4" data-id="element-7342" data-mp="38n25gr90dly7">
          <p className="font-bold text-slate-800" data-id="element-7343" data-mp="3dm5badwtbw78">Het register is leeg</p>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-600" data-id="element-7344" data-mp="3dohokdrmlidy">
            Daardoor krijgt elke leerkracht bij het registreren alleen de
            aanvraagroute te zien. Dat werkt, maar het betekent dat er nog geen
            enkele schoolnaam natrekbaar is.
          </p>
        </div>}

      {schools.length > 0 && <ul className="mt-4 divide-y divide-slate-100" data-id="element-7345" data-mp="2uvnjcbjt14mx">
          {schools.map(school => <li key={school.id} className="flex flex-wrap items-center justify-between gap-3 py-3" data-id="element-7346" data-mp="2fywnudd94d2l">
              <div className="min-w-0" data-id="element-7347" data-mp="2kxpzs1cw3w0n">
                <p className="flex items-center gap-2 font-bold text-slate-900" data-id="element-7348" data-mp="2kwoc8xeoth21">
                  <Building2 className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" data-id="element-7349" data-mp="30gaiylisz3xw" />
                  <span className="truncate" data-id="element-7350" data-mp="2we5qhq43cdcu">{school.name}</span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${STATUS_STYLE[school.status]}`} data-id="element-7351" data-mp="2wckxw61c74hm">
                    {STATUS_LABEL[school.status]}
                  </span>
                </p>
                <p className="mt-0.5 pl-6 text-xs text-slate-500" data-id="element-7352" data-mp="2hgwl3shyd924">
                  {school.city ?? 'plaats onbekend'}
                  {school.brin ? ` · BRIN ${school.brin}` : ''}
                </p>
                {school.status === 'verified' && !school.dpaSignedAt && <p className="mt-1 pl-6 text-xs leading-relaxed text-amber-800" data-id="element-7353" data-mp="28p2ybobtz243">
                    Geverifieerd zonder vastgelegde verwerkersovereenkomst. Deze
                    school is te kiezen bij registratie, en de grondslag daarvoor
                    is niet in het systeem vastgelegd.
                  </p>}
              </div>
              <div className="flex shrink-0 flex-wrap gap-2" data-id="element-7354" data-mp="1jnvsfcakhlnc">
                {school.status !== 'verified' && <button onClick={() => void handleStatus(school, 'verified')} disabled={busy === school.id} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60" data-id="element-7355" data-mp="1omglmrrej52a">
                    <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" data-id="element-7356" data-mp="10x5rht4k48x4" />
                    Verifiëren
                  </button>}
                {school.status === 'verified' && <button onClick={() => void handleStatus(school, 'pending')} disabled={busy === school.id} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60" data-id="element-7357" data-mp="15wqtydixqzel">
                    <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" data-id="element-7358" data-mp="i6eaq6i8pg8p" />
                    Intrekken
                  </button>}
                {school.status !== 'rejected' && <button onClick={() => void handleStatus(school, 'rejected')} disabled={busy === school.id} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-bold text-rose-700 transition-colors hover:bg-rose-50 disabled:opacity-60" data-id="element-7359" data-mp="3dd227xegsf0m">
                    <ShieldX className="h-3.5 w-3.5" aria-hidden="true" data-id="element-7360" data-mp="3rcbgusn0v12h" />
                    Afwijzen
                  </button>}
              </div>
            </li>)}
        </ul>}
    </section>;
}